import { useEffect, useState } from "react";
import { Camera, Warning, ArrowsOut, Ruler, Sun } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * Photo Framing Guide — inline card that gives the tech before/after tips
 * tailored to the WO description before they open camera. Prevents useless
 * blurry photos that fail owner-statement audits.
 */
export default function PhotoOverlayGuide({ jobId, title = "", description = "" }) {
  const [hints, setHints] = useState(null);

  useEffect(() => {
    if (!jobId) return;
    // Try a lightweight endpoint; fall back to heuristic tips if it 404s
    api.get(`/photo-hints/${jobId}`)
      .then(r => setHints(r.data?.hints || null))
      .catch(() => setHints(heuristic(title, description)));
  }, [jobId, title, description]);

  const list = hints || heuristic(title, description);
  if (!list || list.length === 0) return null;

  return (
    <div data-testid="photo-overlay-guide"
      className="mt-2 p-2 border-2 border-cyan-500 bg-cyan-50 shadow-[3px_3px_0_#0891b2]">
      <div className="text-[10px] font-black uppercase tracking-widest text-cyan-800 flex items-center gap-1">
        <Camera weight="fill" size={12}/> Frame the Shot
      </div>
      <ul className="mt-1 space-y-0.5">
        {list.slice(0, 4).map((h, i) => (
          <li key={i} className="text-[11px] text-slate-800 flex items-start gap-1.5">
            <span className="mt-0.5 text-cyan-600 flex-shrink-0">{h.icon}</span>
            <span>{h.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const ICONS = {
  step_back: <ArrowsOut weight="bold" size={11}/>,
  close_up:  <ArrowsOut weight="bold" size={11} style={{ transform: "rotate(45deg)" }}/>,
  wide:      <ArrowsOut weight="bold" size={11}/>,
  ruler:     <Ruler weight="bold" size={11}/>,
  light:     <Sun weight="bold" size={11}/>,
  warn:      <Warning weight="bold" size={11}/>,
};

function heuristic(title = "", description = "") {
  const t = `${title} ${description}`.toLowerCase();
  const hints = [];
  const has = (k) => t.includes(k);
  if (has("leak") || has("water") || has("drip")) {
    hints.push({ icon: ICONS.wide,     text: "Wide shot: get the source AND the damage in the same frame" });
    hints.push({ icon: ICONS.close_up, text: "Close-up of the wet spot with a ruler or coin for scale" });
    hints.push({ icon: ICONS.warn,     text: "Photograph the ceiling above (or floor below) too" });
  }
  if (has("hole") || has("wall") || has("drywall") || has("crack")) {
    hints.push({ icon: ICONS.ruler,   text: "Include a tape measure or business card next to the hole for scale" });
    hints.push({ icon: ICONS.wide,    text: "Wide shot showing the whole wall so owner sees location" });
  }
  if (has("outlet") || has("switch") || has("electric") || has("gfci")) {
    hints.push({ icon: ICONS.warn,    text: "Turn off breaker before opening — safety first" });
    hints.push({ icon: ICONS.close_up,text: "Close-up on the outlet face with the plate off" });
  }
  if (has("appliance") || has("dryer") || has("washer") || has("fridge") || has("stove")) {
    hints.push({ icon: ICONS.close_up,text: "Grab the model # plate on a separate photo" });
    hints.push({ icon: ICONS.wide,    text: "Pull the appliance out — photo behind it too" });
  }
  if (has("hvac") || has("furnace") || has("boiler") || has("heat")) {
    hints.push({ icon: ICONS.close_up,text: "Model tag + serial on one photo, error code on another" });
  }
  if (has("paint") || has("stain") || has("scuff")) {
    hints.push({ icon: ICONS.light,   text: "Natural light — don't shoot into a lamp or window" });
  }
  if (hints.length === 0) {
    hints.push({ icon: ICONS.wide,     text: "Before: wide shot of the whole area" });
    hints.push({ icon: ICONS.close_up, text: "Close-up on the exact problem" });
    hints.push({ icon: ICONS.wide,     text: "After: same angle as the before shot" });
  }
  return hints;
}
